import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { ModalHeader, ModalBody, ModalFooter, Button, Spinner, Input, addToast, Accordion, AccordionItem } from '@heroui/react';
import { motion } from 'framer-motion';
import { ChevronLeft, Plane, AlertTriangle, Pencil, CheckCircle, XCircle } from 'lucide-react';
import CalendarApi from '../../../api/CalendarApi.js';
import CounterApi from '../../../api/CounterApi.js';
import ResourcesApi from '../../../api/resourcesApi.js';
import { unwrap } from '../utils/apiEnvelope.js';
import { extractEnvelopeWarnings } from '../utils/checkOutWarnings.js';
import { pickCalendarBootstrap } from '../../calendar-common/viewStoreKeys.js';
import { isFlightFollowingEnabled } from '../../calendar-common/flightFollowing.js';
import { AppDatePicker } from '../../resources/components/AppDatePicker.js';
import { cdtToEpoch, epochToCalendarDateTime } from '../utils/dateTime.js';
import { toCheckOutFinishPayload } from '../utils/checkInOutAdapters.js';
import { buildCounterSavePayload } from '../utils/buildCounterSavePayload.js';
import {
	isInspectionOverdue,
	normalizeAffectsAirworthiness
} from '../../resources/utils/inspectionStatus.js';

/**
 * CheckOutScreen — in-modal screen swap for the Check Out flow (Phase 2.1).
 *
 * This is the SHELL + BOOTSTRAP. Subs 4-5 fill in:
 *   - Sub 4: Track Flight inputs (departure / expected arrival when
 *     `org.enableTrackFlight === '1'`).
 *   - Sub 5: submit pipeline + Cancel Check Out wiring + bootstrap body
 *     rendering (W&B warnings, Meters Start editable, Inspections list).
 *
 * Replaces the legacy Day-View redirect at `EventViewModal.js:884-888 +
 * 968` for `goTo('event-checkout')` → users without Day-View access (e.g.
 * `aartemorlov1998@holdshort.com` Member+Pilot+Instructor+Mechanic) can now
 * complete check-out without leaving Month V2.
 *
 * Bootstrap (Phase 2.1 Sub 3) — `Promise.allSettled` parallel fetch:
 *   1. `GET /api/v1/org/{org}/calendar/{id}/check-out-start?timezone=…`
 *      → `{ finishSuccess, counters, aviationResource, flightTime }`.
 *      Backend `CheckOutStartResponseDto` does NOT include inspections.
 *   2. `ResourcesApi.getInspections(aircraftId, eventTz, orgUnique)` (when
 *      the event has an aircraft resource). Legacy parity:
 *      `calendar-day.event-modals.js:374-385` pre-loaded inspections via a
 *      separate `getAircraft()` call before opening the checkout modal.
 *
 * `Promise.allSettled` (NOT `Promise.all`) per Rem16 Sub 7 lesson: one
 * failure (e.g. inspections 403) must NOT blank the whole screen. The
 * check-out-start failure IS critical → surface error inline + toast.
 * Inspections failure → empty list, surface a warn toast only.
 */
function CheckOutScreen({ event, eventId, orgUnique, timezone, eventTypeLabel, onBack, onClose, onSubmitted }) {
	const [loading, setLoading] = useState(true);
	const [criticalError, setCriticalError] = useState(null);
	// A refusal that still came with the meters, e.g. "Pilot's Organization
	// Flight Review expired": shown above the form instead of being swallowed
	// into a blank "No check-out data available." (Julian, #bug-report).
	const [startWarning, setStartWarning] = useState(null);
	const [serverWarnings, setServerWarnings] = useState([]);
	// Bootstrap payload — `null` while loading, then either the unwrapped
	// `CheckOutStartResponseDto` shape or `null` on critical failure.
	const [bootstrap, setBootstrap] = useState(null);
	// Inspections list — empty array when no aircraft resource, when the
	// fetch fails, or before load completes. Sub 5 renders this.
	const [inspections, setInspections] = useState([]);

	// Resolve the aircraft from the event's resource roster. Same picker
	// pattern `EventViewModal` uses for the airworthy badge + discrepancies.
	// Rem21 Sub 8: returns the FULL aircraft entity (Reviewer rev mirror of
	// CheckInScreen) so the new Aircraft header section can read all five
	// fields directly. `aircraftId` is the derived id used by inspections.
	const aircraftEntity = useMemo(() => {
		const resources = Array.isArray(event?.eventResources) ? event.eventResources : [];
		for (const er of resources) {
			const aircraft =
				er?.aircraft ||
				er?.resource?.aircraft ||
				(er?.resource && String(er.resource?.type) === 'aircraft' ? er.resource : null);
			if (aircraft?.id != null) return aircraft;
		}
		return null;
	}, [event?.eventResources]);
	const aircraftId = aircraftEntity?.id ?? null;

	// Rem21 Sub 8 — Aircraft header is a HeroUI Accordion (Rem22 Sub 10).
	// Default collapsed for legacy parity (mirror of CheckInScreen Rem20
	// Sub 11). The Accordion owns its open state internally — dropped the
	// prior `showAircraftDetails` useState.

	// Rem21 Sub 8 — fetch full aircraft DTO via the dedicated endpoint so
	// the Show Details panel renders fields not serialized in
	// `Event::EVENT_READ_GROUP` (registrationNumber / pilotRating /
	// instructorRating / endorsements / medicalCertificate). Same pattern
	// as Rem20 Sub 12 (inspections via dedicated endpoint). Failure → fall
	// back to the minimal aircraft entity from event detail; does NOT
	// block Meters / Submit.
	const [aircraftDetails, setAircraftDetails] = useState(null);
	useEffect(() => {
		if (aircraftId == null) {
			setAircraftDetails(null);
			return;
		}
		let cancelled = false;
		ResourcesApi.getAircraft(aircraftId, orgUnique)
			.then(res => {
				if (cancelled) return;
				const payload = res?.data?.data ?? res?.data ?? null;
				setAircraftDetails(payload && typeof payload === 'object' ? payload : null);
			})
			.catch(err => {
				if (cancelled) return;
				// eslint-disable-next-line no-console
				console.warn('[CheckOutScreen] getAircraft failed:', err?.response?.status || err?.message);
				setAircraftDetails(null);
			});
		return () => { cancelled = true; };
	}, [aircraftId, orgUnique]);
	// Effective aircraft for the header — prefer the dedicated-endpoint DTO
	// when available, otherwise fall back to the embedded one from event detail.
	const aircraftForHeader = aircraftDetails || aircraftEntity;

	const fetchBootstrap = useCallback(async () => {
		if (!eventId) return;
		setLoading(true);
		setCriticalError(null);
		setStartWarning(null);
		setServerWarnings([]);

		const tz = timezone || 'UTC';
		// Rem18 Task 1: pass event's org explicitly so cross-org events
		// (multi-org users) hit the correct Symfony route. `orgUnique` is
		// set by `EventViewModal` to `event.organizationUnique` already.
		const tasks = [
			CalendarApi.checkOutStart(eventId, tz, orgUnique)
		];
		// Only fetch inspections when we have an aircraft to query against.
		// Maintenance / simulator events have no aircraft → skip the request.
		if (aircraftId != null) {
			tasks.push(ResourcesApi.getInspections(aircraftId, tz, orgUnique));
		}

		const results = await Promise.allSettled(tasks);
		const startResult = results[0];
		const inspectionsResult = aircraftId != null ? results[1] : null;

		// Critical: check-out-start. If this fails we can't render the
		// screen at all — surface inline + toast and stop.
		if (startResult.status === 'fulfilled') {
			setServerWarnings(extractEnvelopeWarnings(startResult.value));
			let payload;
			try {
				payload = unwrap(startResult.value);
			} catch (e) {
				// The API answers `success: false` with the reason in `error[]`
				// AND the check-out data (meters, aircraft) in `data`. Keep the
				// data and show the reason, rather than blanking the screen.
				const raw = startResult.value;
				const envelope = raw && typeof raw === 'object' && 'data' in raw ? raw.data : raw;
				const reasons = Array.isArray(envelope?.error)
					? envelope.error.filter(Boolean)
					: envelope?.error
						? [String(envelope.error)]
						: [];
				payload = envelope?.data && typeof envelope.data === 'object' ? envelope.data : null;
				setStartWarning(reasons.length ? reasons.join(' · ') : e?.message || 'Check Out is not allowed');
			}
			setBootstrap(payload || null);
		} else {
			const err = startResult.reason;
			setServerWarnings(extractEnvelopeWarnings(err?.response));
			const msg =
				err?.response?.data?.error?.[0] ||
				err?.message ||
				'Failed to load Check Out data';
			setCriticalError(msg);
			setBootstrap(null);
			addToast({ title: 'Check Out load failed', description: msg, color: 'danger' });
		}

		// Non-critical: inspections. Failure → empty list + warn toast so
		// the user knows the airworthy list will be incomplete. The check-out
		// itself can still proceed.
		if (inspectionsResult) {
			if (inspectionsResult.status === 'fulfilled') {
				let list = [];
				try {
					const inspectionsPayload = unwrap(inspectionsResult.value);
					list = Array.isArray(inspectionsPayload) ? inspectionsPayload : [];
				} catch (e) {
					list = [];
					// eslint-disable-next-line no-console
					console.warn('[CheckOutScreen] inspections envelope error:', e);
				}
				setInspections(list);
			} else {
				const err = inspectionsResult.reason;
				// eslint-disable-next-line no-console
				console.warn('[CheckOutScreen] inspections fetch failed:', err);
				setInspections([]);
				// 403 on inspections is benign (user lacks `inspectionsView`);
				// don't toast — same UX as the airworthy badge on the view
				// screen which silently hides the icon. Other failures get a
				// soft warn so the user knows what's missing.
				if (err?.response?.status !== 403) {
					addToast({
						title: 'Inspections unavailable',
						description: 'Airworthy details may be incomplete.',
						color: 'warning'
					});
				}
			}
		} else {
			setInspections([]);
		}

		setLoading(false);
	}, [eventId, timezone, aircraftId, orgUnique]);

	useEffect(() => {
		fetchBootstrap();
	}, [fetchBootstrap]);

	// ─── Track Flight (Sub 4) ────────────────────────────────────────────────
	// Show only when org has the feature enabled AND event isn't maintenance.
	// Bi-directional recalculation matches legacy
	// `calendar.event-checkout.js:23-165` semantics — changing any one of
	// (departure, flight time, expected arrival) updates the dependent field.
	// Track Flight gate — view-agnostic bootstrap key (the month key is empty
	// on the DAY page) + boolean-tolerant flag check (Symfony sends `true`,
	// legacy sends '1'; `String(true) === '1'` hid the section EVERYWHERE).
	const enableTrackFlight = useSelector(state =>
		isFlightFollowingEnabled(pickCalendarBootstrap(state)?.organizationSettings)
	);
	const isMaintenance = String(event?.type || '').toLowerCase().includes('maint');
	const showTrackFlight = enableTrackFlight && !isMaintenance;

	const tz = timezone || 'UTC';

	const [departureCdt, setDepartureCdt] = useState(null);
	const [arrivalCdt, setArrivalCdt] = useState(null);
	// `flightTimeMin` is the authoritative integer total-minutes value used
	// for math; `flightTimeText` is the controlled `HH:MM` display string
	// (allows the user to type freely before parsing on blur).
	const [flightTimeMin, setFlightTimeMin] = useState(0);
	const [flightTimeText, setFlightTimeText] = useState('00:00');

	const minToHHMM = mins => {
		const m = Math.max(0, Math.round(Number(mins) || 0));
		const hh = Math.floor(m / 60);
		const mm = m % 60;
		return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
	};
	const parseHHMMToMin = text => {
		// Accepts `H:MM`, `HH:MM`, or bare integer minutes. Returns null on
		// unparseable input so the caller can keep the prior value.
		if (text == null) return null;
		const s = String(text).trim();
		if (!s) return 0;
		const colon = s.match(/^(\d{1,3}):(\d{1,2})$/);
		if (colon) {
			const h = Number(colon[1]);
			const m = Number(colon[2]);
			if (!Number.isFinite(h) || !Number.isFinite(m) || m >= 60) return null;
			return h * 60 + m;
		}
		const bare = Number(s);
		return Number.isFinite(bare) ? Math.max(0, Math.round(bare)) : null;
	};

	// Initialise Track Flight defaults once the bootstrap arrives. Legacy
	// defaults: departure = now (when checkout starts), flightTime from
	// `bootstrap.flightTime` (decimal hours) or scheduled (finish - start),
	// expectedArrival = departure + flightTime.
	useEffect(() => {
		if (!showTrackFlight || !bootstrap) return;
		// Departure = NOW, always (owner): a check-out is contemporaneous with
		// the button press — the wheels are leaving now, not at the scheduled
		// start. The ± nudge buttons below cover the minute-or-two lag between
		// engine start and clicking Check Out.
		const departEpoch = Math.floor(Date.now() / 1000);

		let mins = 0;
		const ftHours = Number(bootstrap.flightTime);
		if (Number.isFinite(ftHours) && ftHours > 0) {
			mins = Math.round(ftHours * 60);
		} else if (Number(event?.finishTime) > Number(event?.startTime)) {
			mins = Math.round((Number(event.finishTime) - Number(event.startTime)) / 60);
		}
		const arriveEpoch = departEpoch + mins * 60;

		setDepartureCdt(epochToCalendarDateTime(departEpoch, tz));
		setArrivalCdt(epochToCalendarDateTime(arriveEpoch, tz));
		setFlightTimeMin(mins);
		setFlightTimeText(minToHHMM(mins));
		// Re-run only when the bootstrap or event identity changes — NOT on
		// every Track Flight field edit (would clobber user input).
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [bootstrap, eventId, showTrackFlight, tz]);

	// ±1-minute nudges for Departure — clicking Check Out can lag the actual
	// wheels-up by a minute or two. Keeps flight time, shifts arrival.
	const nudgeDeparture = useCallback(deltaMin => {
		const depEpoch = cdtToEpoch(departureCdt, tz);
		if (depEpoch == null) return;
		const next = depEpoch + deltaMin * 60;
		setDepartureCdt(epochToCalendarDateTime(next, tz));
		setArrivalCdt(epochToCalendarDateTime(next + flightTimeMin * 60, tz));
	}, [departureCdt, tz, flightTimeMin]);

	const handleDepartureChange = useCallback(cdt => {
		setDepartureCdt(cdt);
		if (!cdt) return;
		// Recalc expected arrival = departure + flightTime (keep flight time).
		const depEpoch = cdtToEpoch(cdt, tz);
		if (depEpoch == null) return;
		const arriveEpoch = depEpoch + flightTimeMin * 60;
		setArrivalCdt(epochToCalendarDateTime(arriveEpoch, tz));
	}, [tz, flightTimeMin]);

	const handleArrivalChange = useCallback(cdt => {
		setArrivalCdt(cdt);
		if (!cdt) return;
		const depEpoch = cdtToEpoch(departureCdt, tz);
		const arrEpoch = cdtToEpoch(cdt, tz);
		if (depEpoch == null || arrEpoch == null) return;
		// Recalc flight time = arrival - departure (clamp to non-negative).
		const diff = Math.max(0, Math.round((arrEpoch - depEpoch) / 60));
		setFlightTimeMin(diff);
		setFlightTimeText(minToHHMM(diff));
	}, [tz, departureCdt]);

	const handleFlightTimeBlur = useCallback(() => {
		const parsed = parseHHMMToMin(flightTimeText);
		if (parsed == null) {
			// Restore last-known-good display.
			setFlightTimeText(minToHHMM(flightTimeMin));
			return;
		}
		setFlightTimeMin(parsed);
		setFlightTimeText(minToHHMM(parsed));
		// Recalc expected arrival = departure + parsed flightTime.
		const depEpoch = cdtToEpoch(departureCdt, tz);
		if (depEpoch == null) return;
		setArrivalCdt(epochToCalendarDateTime(depEpoch + parsed * 60, tz));
	}, [flightTimeText, flightTimeMin, departureCdt, tz]);

	// Exposed (read-only) for Sub 5 to consume on submit. The submit will
	// serialise `flightTimeMin / 60` as decimal hours with 10 decimals per
	// legacy `calendar.event-checkout.js:299-344`.
	const trackFlightPayload = useMemo(() => {
		if (!showTrackFlight) return null;
		return {
			departureTime: cdtToEpoch(departureCdt, tz),
			expectedArrivalTime: cdtToEpoch(arrivalCdt, tz),
			flightTime: Number((flightTimeMin / 60).toFixed(10))
		};
	}, [showTrackFlight, departureCdt, arrivalCdt, flightTimeMin, tz]);

	// ─── Meters Start (Sub 5) ────────────────────────────────────────────────
	// Counter values come from `bootstrap.counters` and may be edited inline.
	// Each save round-trips to the Symfony global counter endpoints
	// (`POST /counter/{id}` for aircraft, `POST /counter/simulator/{id}` for
	// simulator) — Rem18 Task 4 replaced the legacy hangars endpoints with
	// these. The body is `CounterRequestDto` (`Assert\NotBlank` on `comments`,
	// `rate` + `enableRate` propagated from the original counter row so backend
	// `CounterManager.php:87-93` doesn't clobber them).
	const [counterEdits, setCounterEdits] = useState({}); // {[id]: {value, comments}}
	const [editingCounterId, setEditingCounterId] = useState(null);
	const [savingCounterId, setSavingCounterId] = useState(null);
	const [localCounters, setLocalCounters] = useState([]);

	useEffect(() => {
		if (!bootstrap) return;
		const list = Array.isArray(bootstrap.counters) ? bootstrap.counters : [];
		setLocalCounters(list);
	}, [bootstrap]);

	const isSimulatorResource = useMemo(() => {
		const resources = Array.isArray(event?.eventResources) ? event.eventResources : [];
		return resources.some(er => {
			return (
				er?.simulator ||
				er?.resource?.simulator ||
				(er?.resource && String(er.resource?.type) === 'simulator')
			);
		});
	}, [event?.eventResources]);

	const startCounterEdit = useCallback(id => {
		const current = localCounters.find(c => String(c.id) === String(id));
		setCounterEdits(prev => ({
			...prev,
			[id]: {
				value: current?.value ?? '',
				comments: ''
			}
		}));
		setEditingCounterId(id);
	}, [localCounters]);

	const cancelCounterEdit = useCallback(id => {
		setEditingCounterId(prev => (prev === id ? null : prev));
		setCounterEdits(prev => {
			const next = { ...prev };
			delete next[id];
			return next;
		});
	}, []);

	const saveCounter = useCallback(async id => {
		const edit = counterEdits[id];
		if (!edit) return;
		// M13 (Rem38) — the comment-required gate was removed after the backender
		// dropped `Assert\NotBlank` on `CounterRequestDto::$comments` (a2ceb97).
		// `comments` is still sent (empty allowed) for the audit log.
		// Rem18 Task 4 — migrated to Symfony `/counter/...` global routes.
		// Aircraft path uses the counter row id in the URL; simulator path
		// uses the simulator resource id. `organizationId` in the body
		// drives the org-access check on these global routes.
		const simulatorId = isSimulatorResource ? (() => {
			const resources = Array.isArray(event?.eventResources) ? event.eventResources : [];
			for (const er of resources) {
				const sim =
					er?.simulator ||
					er?.resource?.simulator ||
					(er?.resource && String(er.resource?.type) === 'simulator' ? er.resource : null);
				if (sim?.id != null) return sim.id;
			}
			return null;
		})() : null;
		const organizationId =
			event?.organizationID ??
			event?.organizationId ??
			event?.organization?.id ??
			null;
		if (organizationId == null) {
			addToast({ title: 'Save failed', description: 'Missing event organization id', color: 'danger' });
			return;
		}
		const oldCounter = localCounters.find(c => String(c?.id) === String(id));
		// Build via the pure utility so the DTO shape is locked by tests
		// (`buildCounterSavePayload.test.js`). Sub 4 fix: `rate` + `enableRate`
		// now propagate from the original counter row to avoid backend clobber.
		const payload = buildCounterSavePayload(oldCounter, edit, {
			eventId,
			organizationId,
			organizationUnique: orgUnique,
			aircraftId,
			isSimulatorResource,
			timezone: tz
		});
		setSavingCounterId(id);
		try {
			if (isSimulatorResource) {
				if (simulatorId == null) {
					addToast({ title: 'Save failed', description: 'No simulator to update', color: 'danger' });
					return;
				}
				await CounterApi.editSimulatorCounter(simulatorId, payload);
			} else {
				// Aircraft: URL uses the counter row id (`id` = `bootstrap.counters[].id`).
				await CounterApi.editAircraftCounter(id, payload);
			}
			// Optimistically apply the new value locally — backend response
			// shape varies; trust the user-entered number until the next
			// bootstrap refresh.
			setLocalCounters(prev => prev.map(c => (
				String(c.id) === String(id) ? { ...c, value: edit.value } : c
			)));
			cancelCounterEdit(id);
			addToast({ title: 'Counter saved', color: 'success' });
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Save failed';
			addToast({ title: 'Counter save failed', description: msg, color: 'danger' });
		} finally {
			setSavingCounterId(prev => (prev === id ? null : prev));
		}
	}, [counterEdits, aircraftId, isSimulatorResource, event?.eventResources, event?.organizationID, event?.organizationId, event?.organization?.id, eventId, orgUnique, tz, localCounters, cancelCounterEdit]);

	// ─── Submit (Sub 5) ──────────────────────────────────────────────────────
	const [submitting, setSubmitting] = useState(false);

	const handleSubmit = useCallback(async () => {
		if (!eventId) return;
		// Build the DTO via the Phase 2.1 Sub 1 adapter. Track Flight payload
		// (Sub 4) feeds the optional fields; adapter omits when missing.
		const formState = trackFlightPayload || {};
		const payload = toCheckOutFinishPayload(formState);
		setSubmitting(true);
		try {
			// Rem18 Task 1: cross-org override.
			const res = await CalendarApi.checkOutFinish(eventId, payload, tz, orgUnique);
			const body = res?.data ?? res;
			if (body?.success === false) {
				const msg = (body.error && body.error[0]) || 'Check Out failed';
				addToast({ title: 'Check Out failed', description: msg, color: 'danger' });
				return;
			}
			addToast({ title: 'Checked out', color: 'success' });
			onSubmitted?.();
		} catch (err) {
			const msg = err?.response?.data?.error?.[0] || err?.message || 'Check Out failed';
			addToast({ title: 'Check Out failed', description: msg, color: 'danger' });
		} finally {
			setSubmitting(false);
		}
	}, [eventId, trackFlightPayload, tz, orgUnique, onSubmitted]);

	// Lines come from the response envelope's `warning[]` (inspection expiry,
	// weight-and-balance); the payload carries none. Hidden when empty.
	const warningsPanel =
		serverWarnings.length > 0 ? (
			<div
				className="border border-warning-200 bg-warning-50 text-warning-700 rounded-md p-3 flex gap-2"
				role="alert"
			>
				<AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
				<ul className="text-xs space-y-1 list-disc list-inside">
					{serverWarnings.map((w, i) => (
						<li key={`${i}-${w}`}>{w}</li>
					))}
				</ul>
			</div>
		) : null;

	return (
		<>
			<ModalHeader className="flex flex-col border-b border-default-200 pr-10">
				<div className="flex items-center gap-2 text-xs text-default-500 mb-1">
					<span>{eventTypeLabel || 'Regular Event'}</span>
					<span>/</span>
					<span>Check Out</span>
				</div>
				<div className="flex items-center gap-3">
					<Button isIconOnly size="sm" variant="light" onPress={onBack} aria-label="Back to event">
						<ChevronLeft className="w-4 h-4" />
					</Button>
					<span style={{ color: 'hsl(var(--heroui-primary))' }}>
						<Plane className="w-5 h-5" />
					</span>
					<span className="text-lg font-bold flex-1" style={{ color: 'hsl(var(--heroui-foreground))' }}>
						Check Out
					</span>
				</div>
			</ModalHeader>

			<ModalBody className="pt-4">
				<motion.div
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					transition={{ duration: 0.2, ease: [0.23, 1, 0.32, 1] }}
				>
					{loading && (
						<div className="flex items-center justify-center py-12 gap-3">
							<Spinner size="sm" />
							<span className="text-sm text-default-500">Loading Check Out…</span>
						</div>
					)}
					{!loading && criticalError && (
						<>
							<div className="py-8 text-center text-sm text-danger">{criticalError}</div>
							{warningsPanel}
						</>
					)}
					{!loading && !criticalError && startWarning && (
						<div
							role="alert"
							className="mb-4 rounded-md border border-danger-200 bg-danger-50 px-3 py-2 text-sm text-danger-700"
						>
							<span className="font-semibold">Can&apos;t check out: </span>
							{startWarning}
						</div>
					)}
					{!loading && !criticalError && (
						<div className="text-sm text-default-600 space-y-4">
							{/* Rem22 Sub 10 — Aircraft header as HeroUI Accordion
							    (replaces the prior custom toggle + manual
							    `showAircraftDetails` state). Mirror of CheckInScreen.
							    Default collapsed. Title carries airworthy ✓ (Sub 8). */}
							{aircraftEntity && (
								<section className="border border-default-200 rounded-md overflow-hidden">
								{/* Rem24 Sub 5 — canonical METERS-style border via outer
								    section wrapper; Accordion is `variant="light"` to
								    avoid the prior double-border look. Mirror of CheckIn. */}
								<Accordion
									variant="light"
									className="px-0"
									isCompact
								>
									<AccordionItem
										key="aircraft-details"
										aria-label="Aircraft details"
										classNames={{
											/* Rem22 Sub 13 — mirror CheckInScreen padding/border. */
											base: 'shadow-none',
											trigger: 'px-3 py-2',
											content: 'px-3 py-2 pt-0',
											title: 'text-sm font-medium'
										}}
										title={
											<span className="text-sm font-medium text-default-800 flex items-center gap-1">
												<span className="text-default-500 mr-1">Aircraft:</span>
												{aircraftForHeader.registrationNumber || aircraftForHeader.regNumb || aircraftForHeader.name || `Aircraft #${aircraftForHeader.id}`}
												{/* Rem22 Sub 8 — `inspectionStatus` fallback chain.
												    Rem22 Sub 12 — unified canonical airworthy icon
												    (`CheckCircle` from lucide-react with primary
												    project styling) matching EventViewModal
												    Resources subpanel render at
												    `EventViewModal.js:1384`. */}
												{String(aircraftEntity?.inspectionStatus ?? aircraftDetails?.inspectionStatus ?? '').toLowerCase() === 'airworthy' && (
													<CheckCircle
														className="w-4 h-4 text-success flex-shrink-0"
														fill="currentColor"
														stroke="white"
														strokeWidth={2.5}
														aria-label="Airworthy"
													/>
												)}
											</span>
										}
									>
										<ul className="divide-y divide-default-100">
											{[
												{ label: 'Registration Number', value: aircraftForHeader.registrationNumber || aircraftForHeader.regNumb || aircraftForHeader.name },
												{ label: 'Medical Certificate Required', value: aircraftForHeader.medicalCertificate?.name ?? aircraftForHeader.medicalCertificate ?? aircraftForHeader.medicalCertRequired ?? aircraftForHeader.medicalGrade },
												{ label: 'Endorsements', value: typeof aircraftForHeader.endorsements === 'object' && aircraftForHeader.endorsements != null
													? Object.entries(aircraftForHeader.endorsements).filter(([, v]) => v).map(([k]) => k).join(', ') || '—'
													: aircraftForHeader.endorsements },
												{ label: 'Pilot Rating Required', value: aircraftForHeader.pilotRating?.name ?? aircraftForHeader.pilotRatingRequired ?? aircraftForHeader.pilotRating },
												{ label: 'Instructor Rating Required', value: aircraftForHeader.instructorRating?.name ?? aircraftForHeader.instructorRatingRequired ?? aircraftForHeader.instructorRating }
											].map(row => {
												const display = row.value == null || row.value === '' || row.value === false
													? '—'
													: (row.value === true ? 'Yes' : String(row.value));
												return (
													<li key={row.label} className="px-3 py-2 grid grid-cols-12 gap-2 items-center text-xs">
														<span className="col-span-5 text-default-600">{row.label}</span>
														<span className="col-span-7 text-default-800">{display}</span>
													</li>
												);
											})}
										</ul>
									</AccordionItem>
								</Accordion>
								</section>
							)}

							{warningsPanel}

							{/* Meters Start table — legacy
							    `calendar.event-checkout.js:250-340`. Each row
							    supports inline edit → server save via the
							    legacy hangars endpoints (parent backend ask R''). */}
							{localCounters.length > 0 && (
								<section className="border border-default-200 rounded-md overflow-hidden">
									<header className="px-3 py-2 bg-default-50 border-b border-default-200 flex items-center justify-between">
										<span className="text-xs font-semibold uppercase tracking-wide text-default-500">Meters</span>
										<span className="text-xs font-semibold uppercase tracking-wide text-default-500">Start</span>
									</header>
									<ul className="divide-y divide-default-100">
										{localCounters.map(c => {
											const id = c.id;
											const isEditing = String(editingCounterId) === String(id);
											const edit = counterEdits[id] || {};
											const saving = String(savingCounterId) === String(id);
											return (
												<li key={id} className="px-3 py-2 flex items-center gap-3">
													<span className="font-medium text-default-800 flex-1">
														{c.name || c.label || `Counter #${id}`}
													</span>
													{isEditing ? (
														<form
															className="flex items-center gap-2 flex-wrap"
															onSubmit={e => { e.preventDefault(); saveCounter(id); }}
														>
															<Input
																size="sm"
																aria-label="Counter value"
																type="number"
																value={String(edit.value ?? '')}
																onValueChange={v => setCounterEdits(prev => ({
																	...prev,
																	[id]: { ...prev[id], value: v }
																}))}
																className="w-24"
															/>
															{/* M13 — Comment input removed (legacy has none;
															    backend NotBlank dropped a2ceb97). */}
															<Button
																size="sm"
																type="submit"
																color="success"
																isLoading={saving}
															>Save</Button>
															<Button size="sm" type="button" variant="bordered" onPress={() => cancelCounterEdit(id)}>Cancel</Button>
														</form>
													) : (
														<>
															<span className="font-mono text-default-700">{c.value ?? '—'}</span>
															<Button
																size="sm"
																variant="light"
																isIconOnly
																aria-label={`Edit ${c.name || 'counter'}`}
																onPress={() => startCounterEdit(id)}
															>
																<Pencil className="w-4 h-4" />
															</Button>
														</>
													)}
												</li>
											);
										})}
									</ul>
								</section>
							)}

							{/* Inspections list — legacy `calendar.event-inspections.js`.
							    Color-code overdue (danger) / warning (yellow). */}
							{inspections.length > 0 && (
								<section className="border border-default-200 rounded-md overflow-hidden">
									<header className="px-3 py-2 bg-default-50 border-b border-default-200">
										<span className="text-xs font-semibold uppercase tracking-wide text-default-500">Inspections</span>
									</header>
									<ul className="divide-y divide-default-100">
										{inspections.map(ins => {
											const overdue = isInspectionOverdue(ins);
											const affectsAirworthy = normalizeAffectsAirworthiness(ins.affectsAirworthiness);
											const warn = !overdue && (ins.warning === '1' || ins.warning === true || ins.warning === 1);
											const rowClass = overdue
												? 'text-danger'
												: warn
													? 'text-warning'
													: '';
											// Sub 12 — `ResourcesApi.getInspections` ships fields
											// under AIRCRAFT_INSPECTION_READ_GROUP: `inspectionName`
											// (preferred) and `dueIn` as a backend-formatted human
											// string (e.g. "5 months", "200 hours", "overdue").
											// Use that string as-is per InspectionsPage parity;
											// fall back to date fields only when `dueIn` is absent
											// (e.g. legacy event-detail-embedded inspection arrays).
											const inspectionName = ins.inspectionName || ins.name || ins.label || '—';
											const dueText = ins.dueIn || ins.expirationDate || ins.dueDate || '—';
											return (
												<li key={ins.id} className={`px-3 py-2 flex items-center gap-3 ${rowClass}`}>
													<span className="flex-1 font-medium">{inspectionName}</span>
													<span className="text-xs">{dueText}</span>
													{affectsAirworthy && (
														overdue
															? <XCircle className="w-4 h-4 text-danger" aria-label="Affects airworthiness — overdue" />
															: <CheckCircle className="w-4 h-4 text-success" aria-label="Affects airworthiness" />
													)}
												</li>
											);
										})}
									</ul>
								</section>
							)}

							{bootstrap == null && (
								<p className="text-default-400 italic">No check-out data available.</p>
							)}
							{showTrackFlight && (
								<section className="border-t border-default-200 pt-4 space-y-3">
									<h3 className="text-sm font-semibold text-default-700">Track Flight</h3>
									<p className="text-xs text-default-500">
										All times in event location timezone ({tz}). Changing any field
										recalculates the others.
									</p>
									<div className="grid grid-cols-1 md:grid-cols-3 gap-3">
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Departure</label>
											<div className="flex items-center gap-1">
												<Button
													size="sm"
													variant="bordered"
													isIconOnly
													aria-label="Departure one minute earlier"
													onPress={() => nudgeDeparture(-1)}
												>
													−
												</Button>
												<AppDatePicker
													aria-label="Departure"
													granularity="minute"
													hourCycle="h23"
													hideTimeZone
													value={departureCdt}
													onChange={handleDepartureChange}
												/>
												<Button
													size="sm"
													variant="bordered"
													isIconOnly
													aria-label="Departure one minute later"
													onPress={() => nudgeDeparture(1)}
												>
													+
												</Button>
											</div>
										</div>
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Flight Time (HH:MM)</label>
											<Input
												aria-label="Flight Time"
												value={flightTimeText}
												onValueChange={setFlightTimeText}
												onBlur={handleFlightTimeBlur}
												placeholder="01:30"
											/>
										</div>
										<div className="flex flex-col gap-1">
											<label className="text-xs font-medium text-default-600">Expected Arrival</label>
											<AppDatePicker
												aria-label="Expected Arrival"
												granularity="minute"
												hourCycle="h23"
												hideTimeZone
												value={arrivalCdt}
												onChange={handleArrivalChange}
											/>
										</div>
									</div>
								</section>
							)}
						</div>
					)}
				</motion.div>
			</ModalBody>

			<ModalFooter className="justify-between gap-2 flex-wrap border-t border-default-200 pt-3">
				<Button size="sm" variant="bordered" onPress={onBack}>
					Back
				</Button>
				<div className="flex gap-2 flex-wrap">
					<Button
						size="sm"
						color="success"
						isLoading={submitting}
						isDisabled={loading || !!criticalError || !!startWarning}
						onPress={handleSubmit}
					>
						Check Out
					</Button>
					<Button size="sm" variant="bordered" onPress={onClose}>
						Close
					</Button>
				</div>
			</ModalFooter>
		</>
	);
}

export default CheckOutScreen;
